import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Printer, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrintResultsPage() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState("Term 1");
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const terms = ["Term 1", "Term 2", "Term 3"];

  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const classList = Array.isArray(response.data) ? response.data : [];
      setClasses(classList);
      if (classList.length > 0) {
        setSelectedClass(classList[0].id);
        fetchStudents(classList[0].id, "Term 1");
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Failed to load classes");
      setClasses([]);
    }
  };

  const fetchStudents = async (classId, term) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/results/print-data/${classId}/${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const studentList = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.students) ? response.data.students : []);
      setStudents(studentList);
      setSelectedStudents(new Set()); // Reset selection
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to load students");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = (classId) => {
    setSelectedClass(classId);
    fetchStudents(classId, selectedTerm);
  };

  const handleTermSelect = (term) => {
    setSelectedTerm(term);
    if (selectedClass) {
      fetchStudents(selectedClass, term);
    }
  };

  const handleStudentToggle = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === students.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(students.map(s => s.id)));
    }
  };

  const handlePrintResults = async () => {
    if (selectedStudents.size === 0) {
      toast.error("Please select at least one student");
      return;
    }

    try {
      setPrinting(true);
      const response = await axios.post(`${API}/results/print-pdf`, {
        classId: selectedClass,
        term: selectedTerm,
        studentIds: Array.from(selectedStudents),
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);

      toast.success("Results generated successfully!");
    } catch (error) {
      console.error("Error printing results:", error);
      toast.error("Failed to print results");
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintAllClass = async () => {
    try {
      setPrinting(true);
      const response = await axios.post(`${API}/results/print-pdf`, {
        classId: selectedClass,
        term: selectedTerm,
        studentIds: students.map(s => s.id),
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);

      toast.success("Results generated successfully!");
    } catch (error) {
      console.error("Error printing results:", error);
      toast.error("Failed to print results");
    } finally {
      setPrinting(false);
    }
  };

  const getPositionBadgeColor = (position) => {
    if (position === 1) return "bg-yellow-100 text-yellow-800";
    if (position === 2) return "bg-gray-200 text-gray-800";
    if (position === 3) return "bg-orange-100 text-orange-800";
    return "bg-slate-100 text-slate-800";
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Printer className="w-8 h-8" />
          Print Student Results
        </h1>
        <p className="text-gray-600 mt-2">
          Select and print student result sheets with all details
        </p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="class-select">Class *</Label>
          <Select value={selectedClass || ""} onValueChange={handleClassSelect}>
            <SelectTrigger id="class-select">
              <SelectValue placeholder="Select a class" />
            </SelectTrigger>
            <SelectContent>
              {classes.map((cls) => (
                <SelectItem key={cls.id} value={cls.id}>
                  {cls.display_name || cls.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="term-select">Term *</Label>
          <Select value={selectedTerm} onValueChange={handleTermSelect}>
            <SelectTrigger id="term-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {terms.map((term) => (
                <SelectItem key={term} value={term}>
                  {term}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end">
          <Button
            onClick={handlePrintAllClass}
            disabled={printing || students.length === 0}
            className="w-full gap-2"
          >
            <Download className="w-4 h-4" />
            Print All Class
          </Button>
        </div>
      </div>

      {/* Students List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              Students ({selectedStudents.size} of {students.length} selected)
            </CardTitle>
            {students.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedStudents.size === students.length ? "Deselect All" : "Select All"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <p className="text-gray-500">Loading students...</p>
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No students found for this class
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedStudents.size === students.length && students.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Student ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Total Score</TableHead>
                    <TableHead>Subjects</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedStudents.has(student.id)}
                          onCheckedChange={() => handleStudentToggle(student.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{student.student_id_code}</TableCell>
                      <TableCell>
                        {student.first_name} {student.last_name}
                      </TableCell>
                      <TableCell>{student.gender}</TableCell>
                      <TableCell>
                        <Badge className={getPositionBadgeColor(student.position)}>
                          {student.position}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {Number(student.total_score).toFixed(0)}
                      </TableCell>
                      <TableCell>{student.subject_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print Selected */}
      {selectedStudents.size > 0 && (
        <div className="flex gap-4">
          <Button
            onClick={handlePrintResults}
            disabled={printing}
            className="gap-2 flex-1"
            size="lg"
          >
            <Printer className="w-5 h-5" />
            Print Selected ({selectedStudents.size}) Students
          </Button>
        </div>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">What Will Be Printed</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-900 space-y-2">
          <p>✓ Student name, ID, and class</p>
          <p>✓ Class position and total score</p>
          <p>✓ All subject details (name, score, grade, percentage)</p>
          <p>✓ Parent information</p>
          <p>✓ Cresabel watermark on background</p>
          <p>✓ One page per student (printable A4 format)</p>
        </CardContent>
      </Card>
    </div>
  );
}
